<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from "vue";

import SvgIcon from "./SvgIcon.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  isOpen: { type: Boolean, required: true },
  title: { type: String, default: "" },
  description: { type: String, required: true },
  size: {
    type: String,
    default: "medium",
    validator: (s) => ["small", "medium", "large", "wide"].includes(s),
  },
  wide: { type: Boolean, default: false },
  id: { type: String, default: undefined },
  showClose: { type: Boolean, default: true },
  // Off for a body that runs edge-to-edge - full-bleed dividers, panels that
  // own their own padding. The body still scrolls either way.
  bodyPadded: { type: Boolean, default: true },
  closeOnBackdrop: { type: Boolean, default: true },
  focus: { type: String, default: "" },
});

const emit = defineEmits(["close", "closed"]);

const root = ref(null);
const dialogRef = ref(null);
const titleId = useId();

const close = () => emit("close");

const onBackdropClick = (e) => {
  if (props.closeOnBackdrop && e.target === e.currentTarget) close();
};

const onKeydown = (e) => {
  if (props.isOpen && e.key === "Escape") close();
};

watch(
  () => props.isOpen,
  async (open) => {
    if (!open) return;
    await nextTick();
    if (props.focus) {
      root.value?.querySelector(props.focus)?.focus();
    } else {
      dialogRef.value?.focus();
    }
  },
);

onMounted(() => document.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));
</script>

<template>
  <Teleport to="body">
    <Transition name="modal" @after-leave="emit('closed')">
      <div
        v-if="isOpen"
        ref="root"
        v-focus-trap
        v-bind="$attrs"
        class="backdrop"
        role="presentation"
        @click="onBackdropClick"
      >
        <div
          :id
          ref="dialogRef"
          class="dialog"
          :class="[wide ? 'size-wide' : `size-${size}`]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? titleId : undefined"
          :aria-description="description"
          tabindex="-1"
        >
          <header v-if="title || showClose" class="header">
            <h2 v-if="title" :id="titleId" class="title">{{ title }}</h2>
            <button v-if="showClose" type="button" class="close" aria-label="Close" @click="close">
              <SvgIcon name="system/close" width="16" height="16" />
            </button>
          </header>

          <div class="body" :class="{ padded: bodyPadded }">
            <slot />
          </div>

          <footer v-if="$slots.actions" class="modal-actions">
            <slot name="actions" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use "@/styles/layers";
@use "@/colors" as c;

.backdrop {
  position: fixed;
  inset: 0;
  z-index: layers.$modal;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(26 26 31 / 50%);
  backdrop-filter: blur(2px);
  padding: 24px;
  overflow-y: auto;

  // Stop scroll inertia from bubbling out to the page underneath when
  // the user reaches the top/bottom of the modal.
  overscroll-behavior: contain;
}

.dialog {
  position: relative;
  background: c.$white;
  border-radius: 12px;
  box-shadow:
    0 20px 50px rgb(26 26 31 / 25%),
    0 0 0 1px rgb(26 26 31 / 4%);
  width: 100%;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  outline: none;
}

.size-small {
  max-width: 420px;
}

.size-medium {
  max-width: 560px;
}

.size-large {
  max-width: 800px;
}

.size-wide {
  max-width: 1100px;
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 12px;
  flex-shrink: 0;
}

.title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: c.$text;
  flex: 1;
  min-width: 0;
}

.close {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 6px;
  margin: -6px;
  border-radius: 6px;
  color: c.$text-subtle;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover {
    color: c.$text;
    background: c.$background-grey-light;
  }

  &:focus-visible {
    outline: 2px solid c.$blue;
    outline-offset: 2px;
  }
}

.body {
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1;
  font-size: 0.875rem;
  color: c.$text;
  line-height: 1.5;

  &.padded {
    padding: 8px 24px 20px;
  }
}

.modal-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 24px 12px;
  border-top: 1px solid c.$border-subtle;
  flex-shrink: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;

  .dialog {
    transition:
      transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.25s ease;
  }
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;

  .dialog {
    transform: translateY(8px) scale(0.98);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .modal-enter-active,
  .modal-leave-active {
    transition: none;

    .dialog {
      transition: none;
    }
  }
}
</style>
